import { useCallback, useEffect } from 'react';
import {
	BooleanInput,
	NumberInput,
	TextInput,
	useTranslate
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { InputAdornment, Stack, Typography } from '@mui/material';
import { INPUT_SOURCE } from '../../../constants';

export const AreaInputs = () => {
	const translate = useTranslate();
	const { getValues, setValue } = useFormContext();
	const useFormValues = () =>
		Object.values({
			...useWatch({
				name: [
					INPUT_SOURCE.OFFICE_RENT_AREA,
					INPUT_SOURCE.TERRACE_RENT_AREA,
					INPUT_SOURCE.TERRACE
				]
			}),
			...getValues([
				INPUT_SOURCE.OFFICE_RENT_AREA,
				INPUT_SOURCE.TERRACE_RENT_AREA,
				INPUT_SOURCE.TERRACE
			])
		});

	const [officeRentArea, terraceRentArea, isTerraceOn] = useFormValues();

	const onChangeHandler = useCallback(
		(fieldName: string, value: string) => {
			const newValue = Number(value) || 0;
			setValue(fieldName, Number(newValue.toFixed(2)));

			if (fieldName === INPUT_SOURCE.OFFICE_USABLE_AREA) {
				const officeCoefficient =
					getValues(INPUT_SOURCE.OFFICE_COEFFICIENT) || 1;

				setValue(
					isTerraceOn ? INPUT_SOURCE.OFFICE_RENT_AREA : INPUT_SOURCE.AREA,
					Number((newValue * officeCoefficient).toFixed(2))
				);
			} else if (fieldName === INPUT_SOURCE.OFFICE_COEFFICIENT) {
				const officeUsableArea =
					getValues(INPUT_SOURCE.OFFICE_USABLE_AREA) || 1;

				setValue(
					isTerraceOn ? INPUT_SOURCE.OFFICE_RENT_AREA : INPUT_SOURCE.AREA,
					Number((newValue * officeUsableArea).toFixed(2))
				);
			} else if (fieldName === INPUT_SOURCE.TERRACE_USABLE_AREA) {
				const terraceCoefficient =
					getValues(INPUT_SOURCE.TERRACE_COEFFICIENT) || 1;

				setValue(
					INPUT_SOURCE.TERRACE_RENT_AREA,
					Number((newValue * terraceCoefficient).toFixed(2))
				);
			} else if (fieldName === INPUT_SOURCE.TERRACE_COEFFICIENT) {
				const terraceUsableArea =
					getValues(INPUT_SOURCE.TERRACE_USABLE_AREA) || 1;

				setValue(
					INPUT_SOURCE.TERRACE_RENT_AREA,
					Number((newValue * terraceUsableArea).toFixed(2))
				);
			}
		},
		[getValues, isTerraceOn, setValue]
	);

	useEffect(() => {
		if (isTerraceOn && officeRentArea) {
			setValue(
				INPUT_SOURCE.AREA,
				(officeRentArea || 0) + (terraceRentArea || 0)
			);
		}
	}, [officeRentArea, terraceRentArea, isTerraceOn, setValue]);

	useEffect(() => {
		if (!isTerraceOn) {
			setValue(INPUT_SOURCE.TERRACE_RENT_AREA, '');
			setValue(INPUT_SOURCE.TERRACE_USABLE_AREA, '');
		}
	}, [isTerraceOn, setValue]);

	return (
		<Stack>
			<Typography variant="h6">Офісне приміщення</Typography>
			<Stack direction="row" gap={2}>
				<NumberInput
					onChange={e =>
						onChangeHandler(INPUT_SOURCE.OFFICE_USABLE_AREA, e.target.value)
					}
					source={INPUT_SOURCE.OFFICE_USABLE_AREA}
					label={translate('form.fields.usableArea')}
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="start">м²</InputAdornment>
						}
					}}
					helperText={false}
				/>
				<NumberInput
					onChange={e =>
						onChangeHandler(INPUT_SOURCE.OFFICE_COEFFICIENT, e.target.value)
					}
					source={INPUT_SOURCE.OFFICE_COEFFICIENT}
					label={translate('form.fields.coefficient')}
					defaultValue={1}
					slotProps={{
						input: {
							endAdornment: <InputAdornment position="start">м²</InputAdornment>
						}
					}}
					helperText={false}
				/>
				<TextInput
					source={
						isTerraceOn ? INPUT_SOURCE.OFFICE_RENT_AREA : INPUT_SOURCE.AREA
					}
					label={translate('form.fields.totalArea')}
					slotProps={{
						input: {
							readOnly: true,
							endAdornment: <InputAdornment position="start">м²</InputAdornment>
						}
					}}
					sx={{
						'& .MuiInputBase-input': {
							cursor: 'default'
						}
					}}
					helperText={false}
				/>
			</Stack>
			<Stack direction="row" justifyContent="flex-end">
				<BooleanInput
					source={INPUT_SOURCE.TERRACE}
					label={translate('form.fields.terrace')}
					helperText={false}
				/>
			</Stack>
			{isTerraceOn && (
				<Stack>
					<Typography variant="h6">Тераса</Typography>
					<Stack direction="row" gap={2}>
						<NumberInput
							onChange={e =>
								onChangeHandler(
									INPUT_SOURCE.TERRACE_USABLE_AREA,
									e.target.value
								)
							}
							source={INPUT_SOURCE.TERRACE_USABLE_AREA}
							label={translate('form.fields.usableArea')}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="start">м²</InputAdornment>
									)
								}
							}}
							helperText={false}
						/>
						<NumberInput
							source={INPUT_SOURCE.TERRACE_COEFFICIENT}
							defaultValue={1}
							label={translate('form.fields.coefficient')}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="start">м²</InputAdornment>
									)
								}
							}}
							helperText={false}
						/>
						<TextInput
							onChange={e =>
								onChangeHandler(INPUT_SOURCE.TERRACE_RENT_AREA, e.target.value)
							}
							source={INPUT_SOURCE.TERRACE_RENT_AREA}
							label={translate('form.fields.totalArea')}
							slotProps={{
								input: {
									readOnly: true,
									endAdornment: (
										<InputAdornment position="start">м²</InputAdornment>
									)
								}
							}}
							sx={{
								'& .MuiInputBase-input': {
									cursor: 'default'
								}
							}}
							helperText={false}
						/>
					</Stack>
					<Stack
						direction="row"
						gap={2}
						mt={2}
						justifyContent="flex-end"
						alignItems="flex-end"
					>
						<Typography variant="h6">
							{translate('form.fields.totalArea')}
						</Typography>
						<TextInput
							source={INPUT_SOURCE.AREA}
							variant="standard"
							label=""
							slotProps={{
								input: {
									readOnly: true,
									endAdornment: (
										<InputAdornment position="start">м²</InputAdornment>
									)
								}
							}}
							sx={{
								flex: '0 0 calc(33.33% - 8px)',
								'& .MuiInputBase-input': {
									cursor: 'default',
									padding: '0 8px'
								}
							}}
							helperText={false}
						/>
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};
